import React, { useState, useRef, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SearchSuggestions } from './SearchSugestions';
import { getVuidFromCookie } from '../../../services/ZaiusService';

// Create a client with proper configuration
const defaultQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: 'always'
    }
  }
});

export type SearchBarInputProps = {
  maxResults: number;
  minCharsToSearch: number;
  onSuggestionClick: (suggestion: string) => void;
  searchUrl?: string;
  placeholder: string;
  onSearch?: (term: string) => void;
  preventSubmit?: boolean;
  initialValue?: string;
  personalizedSuggestionsEndpoint?: string;
};

const SearchBarInputContent = ({
  maxResults = 3,
  searchUrl = '/search',
  placeholder = 'Enter a search term...',
  minCharsToSearch = 3,
  onSearch,
  preventSubmit = false,
  initialValue = '',
  personalizedSuggestionsEndpoint
}: SearchBarInputProps) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [buttonXIsVisible, setButtonXIsVisible] = useState(!!initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<number | null>(null);

  // Get VUID from cookie
  const vuid = getVuidFromCookie() || undefined;

  // Update internal state when initialValue changes
  useEffect(() => {
    setSearchTerm(initialValue);
    setButtonXIsVisible(!!initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSearchIconClick = () => {
    if (inputRef.current) {   // focus on input each time search icon is clicked
      inputRef.current.focus();
    }
    handleSearch(); // Keep existing behavior
  };

  const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);
    setFocusedIndex(-1);

    if (value.length > 0) {
      setButtonXIsVisible(true);
    } else {
      setButtonXIsVisible(false);
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
    setShowSuggestions(true);

    // Call handleSearchTermChange to update state if needed
    if (inputRef.current) {
      const event = { target: { value: inputRef.current.value } } as React.ChangeEvent<HTMLInputElement>;
      handleSearchTermChange(event);
    }
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    blurTimeoutRef.current = window.setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  const handleButtonXClick = () => {
    setSearchTerm('');
    setButtonXIsVisible(false);
    setShowSuggestions(false);
    setFocusedIndex(-1);
  };

  const handleSearch = () => {
    setShowSuggestions(false);
    setFocusedIndex(-1);
    performSearch(searchTerm);
  };

  const performSearch = (term: string) => {
    if (onSearch) {
      onSearch(term);
    } else if (!preventSubmit) {
      const params = new URLSearchParams(window.location.search);
      if (term) {
        params.set('q', term);
      } else {
        params.delete('q');
      }
      window.location.href = `${searchUrl}${params.toString() ? `?${params.toString()}` : ''}`;
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) {
      if (event.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    // Get the total number of suggestions from the DOM
    const totalSuggestions = document.querySelectorAll('[data-suggestion-index]').length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex(prev => (prev < totalSuggestions - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        event.preventDefault();
        if (focusedIndex >= 0) {
          handleSuggestionSelect(focusedIndex);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        event.preventDefault();
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
      case 'Tab':
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    setSearchTerm(suggestion);
    setButtonXIsVisible(true);
    setShowSuggestions(false);
    setFocusedIndex(-1);
    performSearch(suggestion);
  };

  const handleSuggestionSelect = (index: number) => {
    const suggestion = document.querySelector(`[data-suggestion-index="${index}"]`);
    if (suggestion) {
      const text = suggestion.textContent || '';
      const plainText = text.replace(/^[\u2B50\u{1F50D}]\s*/u, '');
      handleSuggestionClick(plainText);
    }
  };

  // Focus input when suggestions are shown
  useEffect(() => {
    if (showSuggestions && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSuggestions]);

  return (
    <>
      <input
        ref={inputRef}
        className="search-bar__panel__top__input-wrapper__input"
        type="search"
        placeholder={placeholder}
        aria-label="Search input"
        value={searchTerm}
        onChange={handleSearchTermChange}
        onKeyDown={handleInputKeyDown}
        role="combobox"
        aria-expanded={showSuggestions}
        aria-controls="search-suggestions"
        aria-activedescendant={focusedIndex >= 0 ? `suggestion-${focusedIndex}` : undefined}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
      />
      {buttonXIsVisible && (
        <button
          className={'search-bar__panel__top__input-wrapper__button-x visible'}
          id="search-bar__close-icon"
          onClick={handleButtonXClick}
          aria-label="Clear search"
        >
          <span className="icon">
            <svg role="presentation">
              <use xlinkHref="#icon-x" />
            </svg>
          </span>
        </button>
      )}
      <button
        className="search-bar__panel__top__input-wrapper__button-search"
        aria-label="Search"
        onClick={handleSearchIconClick}
      >
        <span className="icon">
          <svg focusable="false" role="img" aria-label="Search icon">
            <use xlinkHref="#icon-search" />
          </svg>
        </span>
      </button>
      {showSuggestions && (
        <SearchSuggestions
          maxResults={maxResults}
          searchTerm={searchTerm}
          minCharsToSearch={minCharsToSearch}
          onSuggestionClick={handleSuggestionClick}
          focusedIndex={focusedIndex}
          vuid={vuid}
          personalizedSuggestionsEndpoint={personalizedSuggestionsEndpoint}
          isFocused={isInputFocused}
        />
      )}
    </>
  );
};

const SearchBarInput = (props: SearchBarInputProps) => {
  return (
    <QueryClientProvider client={defaultQueryClient}>
      <SearchBarInputContent {...props} />
    </QueryClientProvider>
  );
};

export default SearchBarInput;
